import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import * as yup from "yup";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
import { useSnackbar } from "notistack";
import { getLocale } from "react-i18nify";

import { Box, Divider, Grid, IconButton } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";

import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";

import {
  createQuotation,
  createQuotationRefOld,
  getQuotationDetail,
} from "src/api/quotation";
import { searchProduct } from "src/api/product";

import device from "src/styles/device";
import font from "src/styles/font";
import { Black, GreyStroke, Primary, White } from "src/styles/color";
import { checkNotEmpty, convertPrice, formatFromElastic } from "src/common";
import { validFileExtensions } from "src/common/file";
import { useDialog } from "src/context/DialogContext";

import Main from "src/components/ui/Container";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import AddressAutocomplete from "src/components/core/AddressAutocomplete";
import Loading from "src/components/ui/Loading";

import UploadPdf from "src/components/quotation/dropzone/UploadPdf";
import ProductDataGrid from "src/modules/quotation/CreateQuotation/ProductTable";

const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  position: relative;
  margin: 0 auto;
  padding: 32px;

  @media ${device.mobileL} {
    padding: 24px 0;
  }
`;

const SpanTitle = styled.div`
  color: #000;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 40px;
  text-align: center;

  @media ${device.mobileL} {
    margin-bottom: 0;
  }
`;

const Content = styled.div`
  width: 100%;
  padding: 24px 40px 40px;
  margin-bottom: 24px;
  border-radius: 24px;
  background-color: ${White};
  border: 1px solid ${GreyStroke};

  .MuiFormControl-root {
    position: relative;
  }
  .MuiFormHelperText-root.Mui-error {
    position: absolute;
    bottom: -20px;
  }

  @media ${device.tabletL} {
    padding: 24px 40px;
  }
  @media ${device.tablet} {
    padding: 24px;
  }
  @media ${device.mobileL} {
    /* padding: 24px; */
    border: none;
  }
`;

const SpanLabel = styled.div`
  font-size: ${font.size.lg};
  color: ${Black};
  font-weight: 500;

  span {
    font-size: ${font.size.lg};
    font-weight: 500;
    color: ${Primary};
  }
`;

const WrapperTableGrid = styled.div`
  width: 100%;
  background-color: var(--white-color);
  border-radius: 24px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);

  @media ${device.tabletL} {
    padding-bottom: 0;
    margin-bottom: 24px;
    /* box-shadow: none; */
    border-radius: 16px;
  }
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const ProductChoiceDetail = styled.div`
  width: 100%;
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 35% 35% 30%;
  align-items: center;
  padding: 5px 0;
`;

const SpanProductDetail = styled.span`
  color: ${({ nameProd }) => (nameProd ? "#252525" : "#6C6C70")};
  font-size: 1rem;
  font-weight: 400;
  text-align: ${({ center }) => (center ? "center" : "left")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ price }) =>
    price &&
    css`
      color: #e13b30;
      font-size: 1.25rem;
      font-weight: 600;

      align-self: flex-end;
      text-align: right;
      grid-column: 3;
      grid-row: 1 / span 2;
    `}
`;

const PriceSummerySection = styled.div`
  width: 100%;
  border-radius: 16px;
  background-color: var(--darkwhite-color);
  /* box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.1); */

  @media ${device.tabletL} {
    /* margin-bottom: 24px; */
    /* box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.10); */
  }
`;

const GroupPrice = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// const PriceLabel = styled.div`
//   font-size: ${font.size.sm};
//   font-weight: 400;
//   color: var(--secondary-color);
// `

const PriceText = styled.div`
  font-size: ${font.size.md};
  font-weight: 400;
  color: ${({ total }) =>
    total ? "var(--primary-color)" : "var(--secondary-color)"};

  @media ${device.tabletL} {
    font-size: ${({ price }) => (price ? font.size.md : font.size.sm)};
  }
`;

const phoneRegExp = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

const validationSchema = yup.object().shape({
  full_name: yup.string().required("Please enter name"),
  email: yup
    .string()
    .email("E-mail is incorrect")
    .test({
      name: "format incorrect",
      message: "E-mail is incorrect",
      test: (value, context) => {
        const regex = /@[^.]*\./;
        if (value !== "" && !regex.test(value)) {
          return false;
        }
        return true;
      },
    })
    .required("Please enter e-mail"),
  phone_number: yup
    .string()
    .matches(phoneRegExp, "Phone number is not valid")
    .required("Please enter phone number"),
  branch: yup.string(),
  tax_id: yup.string().test({
    name: "matches",
    message: "Tax ID must be 13 digits",
    test: (value) => {
      const regex = /^\d{13}$/;
      if (value !== "" && !regex.test(value)) {
        return false;
      }
      return true;
    },
  }),
  fax: yup.string(),
  address: yup.string().required("Please enter address"),
  sub_district: yup.string().required("Please enter sub-district"),
  district: yup.string().required("Please enter district"),
  province: yup.string().required("Please enter province"),
  postal_code: yup
    .string()
    .required("Please enter postal code")
    .matches(/^\d{5}$/, "Postal code must be 5 digits"),

  product_choice_quotations: yup
    .array()
    .of(
      yup.object({
        product_choice_quotation: yup
          .object()
          .required("Please select a product"),

        volume: yup.number(),
        total: yup.number(),
        price: yup.number(),

        install_detail_group: yup.object({
          install_area: yup.number(),
          service_note: yup.string(),
          service_price: yup.number(),
          total_install: yup.number(),
        }),
      }),
    )
    .test({
      name: "required",
      message: "Product list should not empty product field.",
      test: (array) => {
        if (array.length > 1) {
          return array.every(
            ({ product_choice_quotation }) => !!product_choice_quotation,
          );
        }
        return true;
      },
    })
    .min(1, "Please add at least 1 product.")
    .required("Product list should not empty product field."),
  total_price: yup.number(),
  discount: yup.number(),
  vat: yup.number(),
  total_vat: yup.number(),

  quotation_price_pdf_first: yup.mixed().test({
    name: "required",
    message: "Please upload quotation PDF",
    test: (value, context) => {
      if (!checkNotEmpty(value)) {
        return false;
      }
      return true;
    },
  }),
  quotation_price_pdf_second: yup.mixed().nullable(),
  additional_document_pdf: yup.mixed().nullable(),

  status: yup.number(),
});

const CreateQuotation = ({ open }) => {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();

  const defaultValues = {
    full_name: "", // ชื่อลูกค้า / ชื่อบริษัท
    email: "",
    phone_number: "",
    branch: "",
    tax_id: "",
    fax: "",
    address: "",
    sub_district: "",
    district: "",
    province: "",
    postal_code: "",

    product_choice_quotations: [
      // {
      //   product_choice_quotation: null, // ส่งแค่ _id ของ product_choice
      //   volume: 0,
      //   price: 0,
      //   total: 0,
      //   install_detail_group: {
      //     install_area: 0,
      //     service_note: "",
      //     service_price: 0,
      //     total_install: 0,
      //   },
      // },
    ],

    total_price: 0,
    discount: 0,
    vat: 0,
    total_vat: 0,

    quotation_price_pdf_first: null,
    quotation_price_pdf_second: null,
    additional_document_pdf: null,

    status: 0,
  };
  const [formData, setFormData] = useState(defaultValues);
  const [dataProductList, setDataProductList] = useState([]);
  const [isAddQuotPdf, setIsAddQuotPdf] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  let resolver = useYupValidationResolver(validationSchema);

  const {
    formState: { errors: formErrors, isValid },
    control: formControl,
    watch,
    trigger,
    getValues,
    setValue: setFormValue,
    reset,
  } = useForm({
    resolver,
    defaultValues,
  });

  // console.log("formState -> formErrors", formErrors);
  // console.log("formState -> isValid", isValid);

  const { remove: removeProduct } = useFieldArray({
    control: formControl,
    name: "product_choice_quotations",
  });

  /**
   * @notice after form value change, then set value to formData state
   */
  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      setFormData(values);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  /**
   * @notice trigger validation when formData updated
   */
  useEffect(() => {
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    let quoValue = getValues();

    reset({
      ...quoValue,
      total_price: sumProductPrice(),
      discount: sumDiscount(),
      vat: calculateVat(),
      total_vat: sumTotalPrice() + calculateVat(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.product_choice_quotations.length]);

  const sumProductPrice = () => {
    // const priceArray = formData?.quotation_list?.map(
    //   (product) => (product.price * product.volume)
    // );
    let quoData = getValues();
    let priceArray = [];

    priceArray = quoData?.product_choice_quotations?.map(
      (product) => product.price * product.volume,
    );

    return priceArray?.reduce((a, b) => a + b, 0) || 0 || 0;
  };

  const sumDiscount = () => {
    // const priceArray = formData?.quotation_list?.map(
    //   (product) => (product.discount)
    // );
    let quoData = getValues();
    let priceArray = [];

    priceArray = quoData?.product_choice_quotations?.map(
      (product) => product.discount,
    );
    return priceArray?.reduce((a, b) => a + b, 0) || 0;
  };

  const sumTotalPrice = () => {
    return sumProductPrice() - sumDiscount() || 0;
  };

  const calculateVat = () => {
    return ((sumProductPrice() - sumDiscount()) * 7) / 100 || 0;
  };

  const fetchQuotationDetail = async () => {
    try {
      setIsLoading(true);
      const { data } = await getQuotationDetail(quotationId);

      if (!!data)
        reset({
          ...data,
          ref_doc: data._id,
          product_choice_quotations: checkNotEmpty(
            data.product_choice_quotations,
          )
            ? data.product_choice_quotations
            : [],

          // ! replace quotation -> remove all pdf
          quotation_price_pdfs: [],
          quotation_price_pdf_first: "",
          quotation_price_pdf_second: "",
          additional_document_pdf: "",

          total_price: sumProductPrice(),
          discount: sumDiscount(),
          vat: calculateVat(),
          total_vat: sumTotalPrice() + calculateVat(),
        });
    } catch (error) {
      console.error("fetch Quotation Detail error", error);
      navigate("/404");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProduct = async () => {
    try {
      setIsLoading(true);

      const { data } = await searchProduct({});

      if (!!data) {
        setDataProductList(
          data?.rows.length > 0
            ? data?.rows?.map((product) => ({
                _id: product?.id,
                product_choice_quotation: formatFromElastic(product),
                volume: 1,
                price:
                  product?.is_start_price === true
                    ? product?.start_price
                    : product?.special_price > 0 &&
                        product?.special_price !== null &&
                        product?.special_price !== undefined
                      ? product?.special_price
                      : product?.price,
                total:
                  product?.is_start_price === true
                    ? product?.start_price
                    : product?.special_price > 0 &&
                        product?.special_price !== null &&
                        product?.special_price !== undefined
                      ? product?.special_price
                      : product?.price,
              }))
            : [],
        );
      }
    } catch (error) {
      console.error("fetch Product List error", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, []);

  useEffect(() => {
    if (!quotationId) return;

    fetchQuotationDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationId]);

  useEffect(() => {
    let quoValue = getValues();

    reset({
      ...quoValue,
      total_price: sumProductPrice(),
      discount: sumDiscount(),
      vat: calculateVat(),
      total_vat: sumTotalPrice() + calculateVat(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.product_choice_quotations.length]);

  const onSubmit = async () => {
    try {
      setIsLoading(true);

      let data = new FormData();
      const {
        quotation_price_pdf_first,
        quotation_price_pdf_second,
        additional_document_pdf,
        ...quoData
      } = formData;

      const createQuoDataParam = {
        ...quoData,
        product_choice_quotations: quoData?.product_choice_quotations.map(
          (product) => ({
            ...product,
            product_choice_quotation: product?.product_choice_quotation?._id,
          }),
        ),
        ...(checkNotEmpty(quotation_price_pdf_first)
          ? { quotation_price_pdf_first }
          : {}),
        ...(checkNotEmpty(quotation_price_pdf_second)
          ? { quotation_price_pdf_second }
          : {}),
        ...(checkNotEmpty(additional_document_pdf)
          ? { additional_document_pdf }
          : {}),
      };

      Object.keys(createQuoDataParam).forEach((key) => {
        if (Array.isArray(createQuoDataParam[key]) || key === "discount") {
          data.append(key, JSON.stringify(createQuoDataParam[key]));
        } else {
          data.append(key, createQuoDataParam[key]);
        }
        return data;
      });

      if (quotationId) {
        const res = await createQuotationRefOld(data);

        if (!!res) {
          navigate("/quotation/list");
        }
      } else {
        const res = await createQuotation(data);

        if (!!res) {
          navigate("/quotation/list");
        }
      }
    } catch (error) {
      console.error("create Quotation error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const isAllowedMedia = (file) => {
    const allowedExtensions = ["pdf"];

    if (!validFileExtensions(file, allowedExtensions)) {
      enqueueSnackbar(
        `File type is not allowed. Only accept ${allowedExtensions.join(
          ", ",
        )}.`,
        { variant: "error" },
      );
      return false;
    } else if (file.size > 2428800) {
      enqueueSnackbar(`File size is too large. Max file size is 2MB`, {
        variant: "error",
      });
      return false;
    }

    return true;
  };

  const handleChange = async (event, replaceName, replaceValue) => {
    // console.log(
    //   "---- e ---",
    //   event,
    //   "===",
    //   replaceName,
    //   "--- replaceValue ---",
    //   replaceValue,
    // );
    let name =
      typeof replaceName !== "undefined" ? replaceName : event.target.name;
    let value =
      typeof replaceValue !== "undefined" ? replaceValue : event.target.value;

    let path = name.split(".");
    let [key, ...rest] = path;

    let editingKey = key;
    let updated;

    if (
      key === "quotation_price_pdf_first" ||
      key === "quotation_price_pdf_second" ||
      key === "additional_document_pdf"
    ) {
      if (value !== null && !isAllowedMedia(value)) return;

      updated = value;
    } else if (key === "product_choice_quotations") {
      let [objIndex, objKey] = rest;

      if (isNaN(objIndex) && value === null) {
        // clear all products
        await reset({
          ...getValues(),
          product_choice_quotations: [],
        });
        return;
      }

      editingKey = `${key}.${objIndex}`;
      let editingRow = getValues(editingKey);

      // console.log('---- editingRow ---', editingRow, '--- value ---', value);

      if (value === null) {
        updated = {
          product_choice_quotation: null,
          volume: 0,
          price: 0,
          // discount: 0,
          total: 0,
        };
      } else if (objKey === "product_choice_quotation") {
        // updated = {
        //   ...editingRow,
        //   product_choice_quotation: value,
        //   volume: 1,
        //   price: value.group_price?.is_start_price
        //     ? value.group_price?.start_price
        //     : value.group_price?.special_price > 0 &&
        //         value.group_price?.special_price !== null &&
        //         value.group_price?.special_price !== undefined
        //       ? value.group_price?.special_price
        //       : value.group_price?.price,
        //   // discount: 0,
        //   total: value.group_price?.is_start_price
        //     ? value.group_price?.start_price
        //     : value.group_price?.special_price > 0 &&
        //         value.group_price?.special_price !== null &&
        //         value.group_price?.special_price !== undefined
        //       ? value.group_price?.special_price
        //       : value.group_price?.price,
        // };
      } else if (objKey === "volume") {
        updated = {
          ...editingRow,
          volume: value,
          total: (
            +editingRow.price * +value -
            (+editingRow?.discount || 0)
          ).toFixed(2),
        };
      } else if (objKey === "discount") {
        updated = {
          ...editingRow,
          discount: value,
          total: +editingRow.price * +editingRow.volume - +value,
        };
      } else {
        updated = {
          ...editingRow,
          [objKey]: value,
        };
      }
    } else {
      updated = value;
    }

    if (editingKey !== "") {
      await setFormValue(editingKey, updated);
    }

    let quoValue = await getValues();

    await reset({
      ...quoValue,
      total_price: sumProductPrice(),
      discount: sumDiscount(),
      vat: calculateVat(),
      total_vat: sumTotalPrice() + calculateVat(),
    });
  };

  const handleSelectProduct = async (value, reason) => {
    let quoData = await getValues();

    await reset({
      ...quoData,
      product_choice_quotations: value,
    });
  };

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
  });

  return (
    <>
      <Main bg="white" open={open}>
        <Container>
          <SpanTitle>แบบฟอร์มคำขอใบเสนอราคา</SpanTitle>

          <Content>
            <Grid container spacing={3} rowSpacing={3} columnSpacing={5}>
              {/* Customer Info */}
              <Grid item xs={12}>
                <SpanLabel>
                  ข้อมูลลูกค้า <span>*</span>
                </SpanLabel>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="ชื่อลูกค้า / ชื่อบริษัท"
                  value={formData?.full_name}
                  onChange={(e) => handleChange(e, "full_name")}
                  error={!!formErrors["full_name"]}
                  helperText={
                    !!formErrors["full_name"]
                      ? formErrors["full_name"].message
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="หมายเลขโทรศัพท์"
                  value={formData?.phone_number}
                  onChange={(e) => handleChange(e, "phone_number")}
                  error={!!formErrors["phone_number"]}
                  helperText={
                    !!formErrors["phone_number"]
                      ? formErrors["phone_number"].message
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="สาขา"
                  value={formData?.branch}
                  onChange={(e) => handleChange(e, "branch")}
                  error={!!formErrors["branch"]}
                  helperText={
                    !!formErrors["branch"] ? formErrors["branch"].message : ""
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="อีเมล"
                  value={formData?.email}
                  onChange={(e) => handleChange(e, "email")}
                  error={!!formErrors["email"]}
                  helperText={
                    !!formErrors["email"] ? formErrors["email"].message : ""
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="เลขประจำตัวผู้เสียภาษี"
                  value={formData?.tax_id}
                  onChange={(e) => handleChange(e, "tax_id")}
                  error={!!formErrors["tax_id"]}
                  helperText={
                    !!formErrors["tax_id"] ? formErrors["tax_id"].message : ""
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="โทรสาร"
                  value={formData?.fax}
                  onChange={(e) => handleChange(e, "fax")}
                  error={!!formErrors["fax"]}
                  helperText={
                    !!formErrors["fax"] ? formErrors["fax"].message : ""
                  }
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <SpanLabel>
                  ที่อยู่ <span>*</span>
                </SpanLabel>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="บ้านเลขที่/หมู่บ้าน/ตึก/ซอย"
                  value={formData?.address}
                  onChange={(e) => handleChange(e, "address")}
                  error={!!formErrors["address"]}
                  helperText={
                    !!formErrors["address"] ? formErrors["address"].message : ""
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <AddressAutocomplete
                  value={
                    checkNotEmpty(formData?.province)
                      ? {
                          province: formData?.province || "",
                          district: formData?.district || "",
                          subdistrict: formData?.sub_district || "", // Little bit different field name from API
                          zipcode: formData?.postal_code || "",
                        }
                      : null
                  }
                  onChange={(value) => {
                    if (value) {
                      handleChange(null, `province`, value.province);
                      handleChange(null, `district`, value.district);
                      handleChange(null, `sub_district`, value.subdistrict); // Little bit different field name from API
                      handleChange(null, `postal_code`, value.zipcode);
                    } else {
                      handleChange(null, `province`, "");
                      handleChange(null, `district`, "");
                      handleChange(null, `sub_district`, ""); // Little bit different field name from API
                      handleChange(null, `postal_code`, "");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      className="TextField-without-border-radius"
                      {...params}
                      label="ตำบล/อำเภอ/จังหวัด/รหัสไปรษณีย์"
                      variant="outlined"
                      fullWidth
                      required
                      sx={{
                        "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                          padding: {
                            xs: "0px 4px 0px 5px",
                            sm: "3px 4px 3px 5px",
                          },
                        },
                      }}
                      error={
                        !!formErrors[`sub_district`] ||
                        !!formErrors[`district`] ||
                        !!formErrors[`province`] ||
                        !!formErrors[`postal_code`]
                      }
                      helperText={
                        (!!formErrors[`sub_district`] ||
                          !!formErrors[`district`] ||
                          !!formErrors[`province`] ||
                          !!formErrors[`postal_code`]) &&
                        "Please select address"
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider
                  sx={{
                    borderWidth: "8px",
                    borderColor: "var(--darkwhite-color)",
                    margin: { xs: "0 -24px", md: "0 -40px" },
                  }}
                />
              </Grid>

              {/* Product List */}
              <Grid item xs={12}>
                <SpanLabel>
                  รายการสินค้า <span>*</span>
                </SpanLabel>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  aria-required={true}
                  multiple
                  id="combo-box-similars"
                  // getOptionSelected={(option, value) =>
                  //   option.product_choice_quotation._id === value.product_choice_quotation._id
                  // }
                  isOptionEqualToValue={(option, value) =>
                    option.product_choice_quotation._id ===
                    value.product_choice_quotation._id
                  }
                  renderOption={(props, option, { selected }) => (
                    <li
                      {...props}
                      key={`product-quotation-${option?.product_choice_quotation?._id}`}
                    >
                      {!!option?.product_choice_quotation?.group_image
                        ?.product_display_image === false ? (
                        <ImageEmpty>
                          <InsertPhotoIcon
                            sx={{
                              color: "rgba(0, 0, 0, 0.54)",
                              cursor: "pointer",
                            }}
                          />
                        </ImageEmpty>
                      ) : (
                        <img
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                            objectPosition: "center",
                            borderRadius: "16px",
                            marginRight: "8px",
                          }}
                          src={
                            option?.product_choice_quotation?.group_image
                              ?.product_display_image
                              ? option?.product_choice_quotation?.group_image?.product_display_image.replace(
                                  ".com",
                                  ".com/300x300",
                                )
                              : ""
                          }
                          alt={
                            option?.product_choice_quotation?.group_image
                              ?.product_display_image
                          }
                        />
                      )}

                      <ProductChoiceDetail>
                        {/* name */}
                        <SpanProductDetail nameProd>
                          {option?.product_choice_quotation?.product?.item_name
                            ? option?.product_choice_quotation?.product
                                ?.item_name
                            : "ไม่มีชื่อ"}
                        </SpanProductDetail>

                        {/* size */}
                        <SpanProductDetail center>
                          {option?.product_choice_quotation?.size?.quantity ===
                          "size"
                            ? `${
                                option?.product_choice_quotation?.size?.width
                              } ${
                                getLocale() === "th"
                                  ? option?.product_choice_quotation?.size
                                      ?.unit_width?.unit_th
                                  : option?.product_choice_quotation?.size
                                      ?.unit_width?.unit_en
                              } x ${
                                option?.product_choice_quotation?.size?.height
                              } ${
                                getLocale() === "th"
                                  ? option?.product_choice_quotation?.size
                                      ?.unit_height?.unit_th
                                  : option?.product_choice_quotation?.size
                                      ?.unit_height?.unit_en
                              } x ${
                                option?.product_choice_quotation?.size
                                  ?.thickness
                              } ${
                                getLocale() === "th"
                                  ? option?.product_choice_quotation?.size
                                      ?.unit_thickness?.unit_th
                                  : option?.product_choice_quotation?.size
                                      ?.unit_thickness?.unit_en
                              }`
                            : option?.product_choice_quotation?.size
                                  ?.quantity === "capacity"
                              ? `${
                                  option?.product_choice_quotation?.size
                                    ?.capacity_size
                                } ${
                                  getLocale() === "th"
                                    ? option?.product_choice_quotation?.size
                                        ?.unit_capacity?.unit_th
                                    : option?.product_choice_quotation?.size
                                        ?.unit_capacity?.unit_en
                                }`
                              : getLocale() === "th"
                                ? "สินค้าสั่งทำพิเศษ"
                                : "Made by order"}
                        </SpanProductDetail>

                        {/* item_code */}
                        <SpanProductDetail>
                          {option?.product_choice_quotation?.product?.item_code
                            ? option?.product_choice_quotation?.product
                                ?.item_code
                            : "-"}
                        </SpanProductDetail>

                        {/* choice name */}
                        <SpanProductDetail center>
                          {option?.product_choice_quotation?.name !== ""
                            ? `( ${option?.product_choice_quotation?.name} )`
                            : `-`}
                        </SpanProductDetail>

                        {/* price */}
                        <SpanProductDetail price>
                          {convertPrice(
                            option?.product_choice_quotation?.group_price
                              ?.is_start_price === true
                              ? option?.product_choice_quotation?.group_price
                                  ?.start_price
                              : option?.product_choice_quotation?.group_price
                                    .special_price > 0 &&
                                  option?.product_choice_quotation?.group_price
                                    .special_price !== null &&
                                  option?.product_choice_quotation?.group_price
                                    .special_price !== undefined
                                ? option?.product_choice_quotation?.group_price
                                    .special_price
                                : option?.product_choice_quotation?.group_price
                                    ?.price,
                          )}
                          {/* {getLocale() === "th"
                            ? ` บาท / ` +
                              option?.product_choice_quotation?.group_price
                                ?.unit_price?.unit_th
                            : ` baht / ` +
                              option?.product_choice_quotation?.group_price
                                ?.unit_price?.unit_en} */}
                        </SpanProductDetail>
                      </ProductChoiceDetail>
                    </li>
                  )}
                  options={dataProductList}
                  value={formData?.product_choice_quotations}
                  onChange={(event, newValue, reason) => {
                    // handleChange(event, "product_choice_quotations.product_choice_quotation", newValue)
                    // if (reason !== "removeOption") appendProduct(newValue);
                    if (
                      formData?.product_choice_quotations?.length > 0 &&
                      newValue.length === 0
                    ) {
                      openDialog({
                        type: "confirm",
                        title: "คุณต้องการลบสินค้าทั้งหมดใช่หรือไม่?",
                        onConfirm: () => {
                          handleChange(null, "product_choice_quotations", null);
                          closeDialog();
                        },
                        maxWidth: 342,
                      });
                    } else {
                      handleSelectProduct([...newValue], reason);
                    }
                  }}
                  getOptionLabel={(option) => {
                    return (
                      option?.product_choice_quotation?.product?.item_code +
                      " - " +
                      option?.product_choice_quotation?.product?.item_name +
                      (option.product_choice_quotation?.name !== ""
                        ? ` - (` + option.product_choice_quotation?.name + `)`
                        : ``)
                    );
                  }}
                  filterOptions={filterOptions}
                  sx={{
                    width: "100%",
                    background: "#fff",
                    borderRadius: "16px",
                    "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                      padding: {
                        xs: "0px 4px 0px 5px",
                        sm: "3px 4px 3px 5px",
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      className="TextField-without-border-radius"
                      {...params}
                      label="สินค้า"
                    />
                  )}
                />
              </Grid>

              {!!formData?.product_choice_quotations &&
                formData?.product_choice_quotations.length > 0 && (
                  <Grid item xs={12}>
                    <WrapperTableGrid>
                      <ProductDataGrid
                        formData={formData}
                        onChange={handleChange}
                        onRemoveElement={removeProduct}
                      />
                    </WrapperTableGrid>
                  </Grid>
                )}

              <Grid item xs={12}>
                <PriceSummerySection>
                  <Grid
                    container
                    sx={{
                      m: 0,
                      width: "100%",
                      px: {
                        md: 3,
                        xs: 2,
                      },
                      py: {
                        md: 3,
                        xs: 2,
                      },
                      ".MuiGrid-item": {
                        mb: {
                          md: 2,
                          xs: 1,
                        },
                        ":last-child": {
                          mb: 0,
                        },
                      },
                    }}
                  >
                    <Grid item xs={12}>
                      <GroupPrice>
                        <PriceText>รวมเงิน</PriceText>

                        <PriceText price={true}>
                          {convertPrice(formData?.total_price || 0)}
                        </PriceText>
                      </GroupPrice>
                    </Grid>

                    {/* <Grid item xs={12}>
                      <GroupPrice>
                        <PriceText>ส่วนลด</PriceText>

                        <PriceText price={true}>
                          {convertPrice(formData?.discount || 0)}
                        </PriceText>
                      </GroupPrice>
                    </Grid>

                    <Grid item xs={12}>
                      <GroupPrice>
                        <PriceText>มูลค่าหลังหักส่วนลด</PriceText>

                        <PriceText price={true}>
                          {convertPrice(
                            +formData?.total_price - +formData?.discount || 0,
                          )}
                        </PriceText>
                      </GroupPrice>
                    </Grid> */}

                    <Grid item xs={12}>
                      <GroupPrice>
                        <PriceText>ราคารวม Vat7%</PriceText>

                        <PriceText price={true}>
                          +{convertPrice(formData?.vat || 0)}
                        </PriceText>
                      </GroupPrice>
                    </Grid>

                    <Grid item xs={12}>
                      <GroupPrice>
                        <PriceText>จำนวนเงินทั้งสิ้น</PriceText>

                        <PriceText price={true} total={true}>
                          {convertPrice(formData?.total_vat || 0)}
                        </PriceText>
                      </GroupPrice>
                    </Grid>
                  </Grid>
                </PriceSummerySection>
              </Grid>
            </Grid>
          </Content>

          <Content>
            <Grid container rowSpacing={3} columnSpacing={5}>
              <Grid item xs={12}>
                <SpanLabel>
                  แนบใบเสนอราคา <span>*</span>
                </SpanLabel>
              </Grid>

              <Grid
                item
                xs={12}
                style={{ display: "flex", alignItems: "center" }}
              >
                <TextField
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <>
                        {!checkNotEmpty(formData?.quotation_price_pdf_first) ? (
                          <UploadPdf
                            callBackFile={(fileCallBlack) =>
                              handleChange(
                                null,
                                "quotation_price_pdf_first",
                                fileCallBlack,
                              )
                            }
                            closeAlert={false}
                          >
                            <PictureAsPdfIcon
                              sx={{
                                color: checkNotEmpty(
                                  formData?.quotation_price_pdf_first,
                                )
                                  ? "#e04132"
                                  : "rgba(0, 0, 0, 0.54)",
                                cursor: "pointer",
                                ":hover": {
                                  color: "#e04132",
                                },
                              }}
                            />
                          </UploadPdf>
                        ) : (
                          <IconButton
                            sx={{ padding: 0 }}
                            onClick={() =>
                              handleChange(
                                null,
                                "quotation_price_pdf_first",
                                null,
                              )
                            }
                          >
                            <CloseIcon
                              sx={{ color: "#e04132", cursor: "pointer" }}
                            />
                          </IconButton>
                        )}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  label="PDF (My Account)"
                  variant="outlined"
                  className="TextField-without-border-radius"
                  sx={{
                    my: 1,
                    width: isAddQuotPdf ? "100%" : "95%",
                    textAlign: "left",
                    background: "#fff",
                    borderRadius: "16px",
                    transition: "0.3s",
                  }}
                  value={
                    !!formData?.quotation_price_pdf_first
                      ? typeof formData?.quotation_price_pdf_first !== "object"
                        ? formData?.quotation_price_pdf_first
                        : formData?.quotation_price_pdf_first?.name
                      : ""
                  }
                  error={!!formErrors["quotation_price_pdf_first"]}
                />

                {!isAddQuotPdf && (
                  <IconButton
                    sx={{
                      padding: 0,
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      border: "1px solid #E13B30",
                      marginLeft: "16px",
                    }}
                    onClick={() => setIsAddQuotPdf(true)}
                  >
                    <AddIcon
                      sx={{
                        color: "#e04132",
                        cursor: "pointer",
                        width: "100%",
                      }}
                    />
                  </IconButton>
                )}
              </Grid>

              {isAddQuotPdf && (
                <Grid
                  item
                  xs={12}
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <TextField
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <>
                          {!checkNotEmpty(
                            formData?.quotation_price_pdf_second,
                          ) ? (
                            <UploadPdf
                              callBackFile={(fileCallBlack) =>
                                handleChange(
                                  null,
                                  "quotation_price_pdf_second",
                                  fileCallBlack,
                                )
                              }
                              closeAlert={false}
                            >
                              <PictureAsPdfIcon
                                sx={{
                                  color: checkNotEmpty(
                                    formData?.quotation_price_pdf_second,
                                  )
                                    ? "#e04132"
                                    : "rgba(0, 0, 0, 0.54)",
                                  cursor: "pointer",
                                  ":hover": {
                                    color: "#e04132",
                                  },
                                }}
                              />
                            </UploadPdf>
                          ) : (
                            <IconButton
                              sx={{ padding: 0 }}
                              onClick={() =>
                                handleChange(
                                  null,
                                  "quotation_price_pdf_second",
                                  null,
                                )
                              }
                            >
                              <CloseIcon
                                sx={{ color: "#e04132", cursor: "pointer" }}
                              />
                            </IconButton>
                          )}
                        </>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                    label="PDF (My Account)"
                    variant="outlined"
                    className="TextField-without-border-radius"
                    sx={{
                      my: 1,
                      width: "95%",
                      textAlign: "left",
                      background: "#fff",
                      borderRadius: "16px",
                    }}
                    value={
                      !!formData?.quotation_price_pdf_second
                        ? typeof formData?.quotation_price_pdf_second !==
                          "object"
                          ? formData?.quotation_price_pdf_second
                          : formData?.quotation_price_pdf_second?.name
                        : ""
                    }
                  />

                  <IconButton
                    sx={{
                      padding: "5px",
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      backgroundColor: "#BCBCC0",
                      marginLeft: "16px",
                    }}
                    onClick={() => setIsAddQuotPdf(false)}
                  >
                    <CloseIcon
                      sx={{
                        color: "#ffffff",
                        cursor: "pointer",
                        width: "100%",
                      }}
                    />
                  </IconButton>
                </Grid>
              )}

              <Grid item xs={12}>
                <SpanLabel>แนบเอกสารเพิ่มเติม</SpanLabel>
              </Grid>

              <Grid
                item
                xs={12}
                style={{ display: "flex", alignItems: "center" }}
              >
                <TextField
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <>
                        {!checkNotEmpty(formData?.additional_document_pdf) ? (
                          <UploadPdf
                            callBackFile={(fileCallBlack) =>
                              handleChange(
                                null,
                                "additional_document_pdf",
                                fileCallBlack,
                              )
                            }
                            closeAlert={false}
                          >
                            <PictureAsPdfIcon
                              sx={{
                                color: checkNotEmpty(
                                  formData?.additional_document_pdf,
                                )
                                  ? "#e04132"
                                  : "rgba(0, 0, 0, 0.54)",
                                cursor: "pointer",
                                ":hover": {
                                  color: "#e04132",
                                },
                              }}
                            />
                          </UploadPdf>
                        ) : (
                          <IconButton
                            sx={{ padding: 0 }}
                            onClick={() =>
                              handleChange(
                                null,
                                "additional_document_pdf",
                                null,
                              )
                            }
                          >
                            <CloseIcon
                              sx={{ color: "#e04132", cursor: "pointer" }}
                            />
                          </IconButton>
                        )}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  label="PDF (My Account)"
                  variant="outlined"
                  className="TextField-without-border-radius"
                  sx={{
                    my: 1,
                    width: "100%",
                    textAlign: "left",
                    background: "#fff",
                    borderRadius: "16px",
                    transition: "0.3s",
                  }}
                  value={
                    !!formData?.additional_document_pdf
                      ? typeof formData?.additional_document_pdf !== "object"
                        ? formData?.additional_document_pdf
                        : formData?.additional_document_pdf?.name
                      : ""
                  }
                />
              </Grid>
            </Grid>
          </Content>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
              px: 4,
              py: 2,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() =>
                openDialog({
                  type: "confirm",
                  title: !!quotationId
                    ? `คุณต้องการสร้างใบเสนอราคาใช่หรือไม่`
                    : `คุณต้องการสร้างใบเสนอราคาใช่หรือไม่`,
                  onConfirm: () => {
                    onSubmit();
                    closeDialog();
                  },
                  maxWidth: 400,
                })
              }
              disabled={!isValid}
              sx={{ width: "100%", maxWidth: "160px" }}
            >
              {getLocale() === "th" ? "สร้าง" : "Create"}
            </Button>
          </Box>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default CreateQuotation;
